import { keepPreviousData, QueryObserverOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { merge } from 'remeda';

import { UnknownObject } from '@tulp';

import { URL_API_BASE } from '../query-fn';
import { UserQueryStore } from '../query-store';
import {
  FeatureAccessDataType,
  MailingPreferencesDataType,
  OrganizationItemListType,
  SubscriptionStatusDataType,
  TopActiveMemberDataType,
  UserDataType
} from '../types';
import { DEFAULT_QUERY_PARAMS, useInfiniteQuery } from './infinite';
import { QueryStringParamsType } from './types';

export function useGetAuthenticatedUser(options?: QueryObserverOptions<UserDataType>) {
  const queryRes = useQuery({ ...UserQueryStore.authenticated, ...((options ?? {}) as UnknownObject) });
  return { ...queryRes, data: queryRes.data as UserDataType };
}

// Alias for useGetAuthenticatedUser for consistency
export const useGetCurrentUser = useGetAuthenticatedUser;

export function useGetAuthUserTotalHookups(options?: QueryObserverOptions<number>) {
  return useQuery({ ...UserQueryStore['auth-user-total-hookups'], ...((options ?? {}) as UnknownObject) });
}

export function useGetAuthUserTopActiveMembers(options?: QueryObserverOptions<TopActiveMemberDataType[]>) {
  return useQuery({ ...UserQueryStore['auth-user-top-active-members'], ...((options ?? {}) as UnknownObject) });
}

export function useGetAuthUserMailingPreferences(options?: QueryObserverOptions<MailingPreferencesDataType>) {
  return useQuery({ ...UserQueryStore['auth-user-mailing-preferences'], ...((options ?? {}) as UnknownObject) });
}

export function useGetUserDetailsByUsernameOrFullname(key: string, options?: QueryObserverOptions<UserDataType>) {
  return useQuery({ ...UserQueryStore['details-by-username-or-fullname'](key), ...((options ?? {}) as UnknownObject) });
}

const UsersDefaultQueryStringParams = {
  page: DEFAULT_QUERY_PARAMS.page,
  size: DEFAULT_QUERY_PARAMS.size,
  sorting_parameter: 'createdOn',
  sorting_direction: 'desc',
  f_fullName: '',
  f_firstName: '',
  f_lastName: '',
  f_username: '',
  f_city: '',
  f_companyName: '',
  'f_location.name': '',
  f_isSubscribe: undefined,
  f_jobPosition: undefined
};

export type UsersSortingParametersType =
  | keyof Pick<UserDataType, 'fullName' | 'lastName' | 'username' | 'companyName' | 'jobPosition' | 'pastMatches' | 'attendedGroupHookups'>
  | 'location.name'
  | 'isSubscribe';

export type UsersQueryParamsType = {
  f_firstName?: string;
  f_lastName?: string;
  f_username?: string;
  'f_location.name'?: string;
  f_companyName?: string;
  f_jobPosition?: string;
  f_tags?: string;
  f_isSubscribe?: boolean;
};

export type UseUsersInfiniteQueryProps = {
  queryStringParams?: QueryStringParamsType<UsersSortingParametersType>;
};

export function useUsersInfiniteQuery(props?: UseUsersInfiniteQueryProps) {
  return useInfiniteQuery<UserDataType, QueryStringParamsType<UsersSortingParametersType> & UsersQueryParamsType>({
    queryKey: ['users', 'infinite'],
    baseUrl: `${URL_API_BASE}/admin/users`,
    defaultQueryStringParams: merge(UsersDefaultQueryStringParams, props?.queryStringParams),
    queryOptions: {
      placeholderData: keepPreviousData
    }
  });
}

export function useGetUserOrganizations(options?: QueryObserverOptions<OrganizationItemListType[]>) {
  return useQuery({ ...UserQueryStore['organizations'], ...((options ?? {}) as UnknownObject) });
}

export function useGetAmbassadors(options?: QueryObserverOptions<UserDataType[]>) {
  return useQuery({ ...UserQueryStore['ambassadors'], ...((options ?? {}) as UnknownObject) });
}

export function useGetPublicAmbassadors(options?: QueryObserverOptions<UserDataType[]>) {
  return useQuery({ ...UserQueryStore['public-ambassadors'], ...((options ?? {}) as UnknownObject) });
}

export function useGetExperts(options?: QueryObserverOptions<UserDataType[]>) {
  return useQuery({ ...UserQueryStore['experts'], ...((options ?? {}) as UnknownObject) });
}

export function useGetPublicExperts(options?: QueryObserverOptions<UserDataType[]>) {
  return useQuery({ ...UserQueryStore['public-experts'], ...((options ?? {}) as UnknownObject) });
}

export function useGetAllRoles(options?: QueryObserverOptions<{ id: string; code: string; name: string }[]>) {
  return useQuery({ ...UserQueryStore['all-roles'], ...((options ?? {}) as UnknownObject) });
}

// Subscription-related query hooks
// These hooks have aggressive refetching to ensure grace period status is always fresh
export function useGetUserSubscriptionStatus(options?: QueryObserverOptions<SubscriptionStatusDataType>) {
  return useQuery({
    ...UserQueryStore['subscription-status'],
    staleTime: 0, // Always consider stale to ensure fresh grace period status
    gcTime: 1000 * 60 * 2, // Cache for only 2 minutes (reduced from default 5)
    refetchOnMount: true, // Refetch when component mounts if data is stale
    refetchOnWindowFocus: true, // Refetch when user returns to tab (important for grace period)
    refetchOnReconnect: true, // Refetch when network reconnects
    ...((options ?? {}) as UnknownObject)
  });
}

export function useGetUserSubscriptionFeatures(options?: QueryObserverOptions<Record<string, boolean>>) {
  return useQuery({
    ...UserQueryStore['subscription-features'],
    staleTime: 0, // Always consider stale
    gcTime: 1000 * 60 * 2, // Cache for only 2 minutes
    refetchOnMount: true, // Refetch when component mounts if data is stale
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    refetchOnReconnect: true, // Refetch when network reconnects
    ...((options ?? {}) as UnknownObject)
  });
}

export function useCheckFeatureAccess(feature: string, options?: QueryObserverOptions<FeatureAccessDataType>) {
  return useQuery({ ...UserQueryStore['feature-access'](feature), ...((options ?? {}) as UnknownObject) });
}

// Invalidation hook for user-related queries
export function useInvalidateUserQueries() {
  const queryClient = useQueryClient();

  return () => {
    // Invalidate all user-related queries
    queryClient.invalidateQueries({ queryKey: ['user'] });
  };
}
